import { IconClock16, IconPinMarker16 } from '@warp-ds/icons/react';
import { Button, TextField } from '@warp-ds/react';
import cn from 'classnames';
import { startOfDay, subDays } from 'date-fns';
import { type FormEvent, useReducer, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { t } from 'shared/locale/texts';
import { storeLastSearchCar } from 'shared/search/common/lastSearch';
import { trackSearchClick } from 'shared/search/common/tracking';
import { FrontpageDatePickerWrapper } from '../../../components/warp/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { buildCarTrackingItem } from '../../../util/tracking';
import actions, { reducer } from '../../car/carActions';
import { type CarState, carDefaultState } from '../../car/carDefaultState';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { carAutocompleteMapper } from '../common/autocomplete/AutocompleteMapper';
import { CustomSelect } from '../common/CustomSelect';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { AgePicker } from './AgePicker';
import styles from './CarSearch.module.css';
import { CarSearchHiddenInputs } from './CarSearchHiddenInputs';
import { useCarInitialState } from './hooks/useCarInitialState';
import { useCarValidation } from './hooks/useCarValidation';
import { usePickupLocationResolver } from './hooks/usePickupLocationResolver';
import { useTimePicker } from './hooks/useTimePicker';

type CarSearchProps = {
    prefill?: Partial<CarState>;
    position: string;
    unleash?: Record<string, boolean> | null; // cannot be just undefined as that is not serializable (toggles are returned from getSSP)
};

export const CarSearch = ({ prefill, unleash, position }: CarSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, carDefaultState);

    const isSmallScreen = useBreakpoints({ breakpoint: 'sm' });
    const { carAutocompleteUrl } = useConfig();

    const { resolvePickupLocation } = usePickupLocationResolver(dispatch);
    const { invalid, validationErrors, getInvalidFields } = useCarValidation(state, pristine);

    useCarInitialState(dispatch, prefill);
    const { allTimes, returnTimeOptions } = useTimePicker({
        pickupDate: state.pickupDate,
        returnDate: state.returnDate,
        pickupTime: state.pickupTime,
        returnTime: state.returnTime,
        onReturnTimeChange: (value) => dispatch(actions.setReturnTime(value)),
        interval: state.interval,
    });

    const formatOptions = (times: string[]) =>
        times.map((time) => ({
            value: time,
            label: time,
            icon: <IconClock16 aria-hidden />,
        }));

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        setPristine(false);

        if (Object.values(getInvalidFields(false)).some(Boolean)) {
            event.preventDefault();
            return;
        }

        trackSearchClick('Search carrental', '/reise/leiebil', 'car_rental', position, buildCarTrackingItem(state), 'Form');
        storeLastSearchCar(state);
    };

    return (
        <SearchPanelWrapper onSubmit={handleSubmit} action="https://leiebil.finn.no/NO/book" method="GET" dataTestId="carSearch">
            <div className={styles['form-header']}>
                <AgePicker value={state.driverAge?.toString() || ''} onChange={(val) => dispatch(actions.setDriverAge(val))} />
                <CustomSelect
                    slim
                    triggerIcon={isSmallScreen ? <IconPinMarker16 /> : undefined}
                    fullscreenTitle="Hvor skal du levere bilen?"
                    data-testid="returnLocationIsSame"
                    value={state.returnLocationIsSame ? 'same' : 'different'}
                    onChange={(e) => dispatch(actions.setReturnLocationIsSame(e === 'same'))}
                    options={[
                        { value: 'same', label: 'Lever samme sted' },
                        { value: 'different', label: 'Annet leveringssted' },
                    ]}
                />
            </div>
            <div className={cn(styles['form-body'], state.returnLocationIsSame && styles['form-body--same-location'])}>
                <div
                    className={cn(styles['form-body__col-wrapper'], {
                        [styles['form-body__col-wrapper--same-location']]: state.returnLocationIsSame,
                    })}>
                    <div className={styles['form-body__col']}>
                        {!state.returnLocationIsSame && <div className={styles['route-divider']}>HENTING</div>}
                        <Autocomplete
                            vertical="car"
                            id="pickupLocation"
                            label={t('car.autocomplete.pickup.label')}
                            placeholder={t('car.autocomplete.pickup.placeholder')}
                            defaultValue={state.pickupLocation}
                            onSelect={(opt) => resolvePickupLocation(opt)}
                            url={carAutocompleteUrl}
                            mapOptions={carAutocompleteMapper}
                            minChars={3}
                            error={validationErrors.pickupLocation}
                        />
                        <div className={styles['form-body__date-time']}>
                            <FrontpageDatePickerWrapper
                                className={styles.FrontpageDatePicker}
                                startDate={state.pickupDate}
                                useRange={false}
                                onDateChange={({ startDate }) => dispatch(actions.setDates({ startDate, endDate: state.returnDate }))}
                                useAnchor={{
                                    title: 'Når skal bilen hentes?',
                                    invalid: invalid.pickupDate,
                                    helpText: validationErrors.pickupDate,
                                }}>
                                <TextField
                                    id="pickupDate"
                                    label="Hentedato"
                                    invalid={invalid.pickupDate}
                                    helpText={validationErrors.pickupDate}
                                />
                            </FrontpageDatePickerWrapper>
                            <CustomSelect
                                relative
                                fullscreenTitle="Når skal bilen hentes?"
                                triggerLabel="Hentetid"
                                value={state.pickupTime}
                                onChange={(e) => dispatch(actions.setPickupTime(e))}
                                options={formatOptions(allTimes)}
                                className={styles['time-select']}
                            />
                        </div>
                    </div>
                    <div className={styles['form-body__col']}>
                        {!state.returnLocationIsSame && (
                            <>
                                <div className={styles['route-divider']}>LEVERING</div>
                                <Autocomplete
                                    departure={false}
                                    vertical="car"
                                    id="returnLocation"
                                    label={t('car.autocomplete.return.label')}
                                    placeholder={t('car.autocomplete.return.placeholder')}
                                    defaultValue={state.returnLocation}
                                    onSelect={(o) => dispatch(actions.setReturnLocation(o))}
                                    url={carAutocompleteUrl}
                                    mapOptions={carAutocompleteMapper}
                                    minChars={3}
                                    error={validationErrors.returnLocation}
                                />
                            </>
                        )}
                        <div className={styles['form-body__date-time']}>
                            <FrontpageDatePickerWrapper
                                className={styles.FrontpageDatePicker}
                                startDate={state.returnDate}
                                useRange={false}
                                onDateChange={({ startDate }) =>
                                    dispatch(actions.setDates({ startDate: state.pickupDate, endDate: startDate }))
                                }
                                disabledDateRanges={
                                    state.pickupDate
                                        ? [{ start: startOfDay(new Date()), end: startOfDay(subDays(state.pickupDate, 1)) }]
                                        : []
                                }
                                useAnchor={{
                                    title: 'Når skal bilen leveres?',
                                    invalid: invalid.returnDate,
                                    helpText: validationErrors.returnDate,
                                }}>
                                <TextField
                                    id="returnDate"
                                    label="Leveringsdato"
                                    invalid={invalid.returnDate}
                                    helpText={validationErrors.returnDate}
                                />
                            </FrontpageDatePickerWrapper>
                            <CustomSelect
                                relative
                                fullscreenTitle="Når skal bilen leveres?"
                                triggerLabel="Leveringstid"
                                value={state.returnTime}
                                onChange={(e) => dispatch(actions.setReturnTime(e))}
                                options={formatOptions(returnTimeOptions)}
                                className={styles['time-select']}
                            />
                        </div>
                    </div>
                </div>
                <Button
                    // @ts-ignore: TODO typedefs from @fabric-ds is confusing the build somehow.. remove when fabric is gone.
                    fullWidth
                    primary
                    type="submit"
                    className={cn(styles['search-button'], {
                        [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                    })}>
                    {t('action.search')}
                </Button>
            </div>
            <CarSearchHiddenInputs
                driverAge={state.driverAge}
                pickupDate={state.pickupDate}
                pickupTime={state.pickupTime}
                returnDate={state.returnDate}
                returnTime={state.returnTime}
                pickupLocationId={state.pickupLocationId}
                pickupLocationLatitude={state.pickupLocationLatitude}
                pickupLocationLongitude={state.pickupLocationLongitude}
                pickupLocation={state.pickupLocation}
                returnLocationIsSame={state.returnLocationIsSame}
                returnLocationId={state.returnLocationId}
                returnLocationLatitude={state.returnLocationLatitude}
                returnLocationLongitude={state.returnLocationLongitude}
                returnLocation={state.returnLocation}
                unleash={unleash}
            />
        </SearchPanelWrapper>
    );
};
